import { AxiosError } from 'axios'
import { t } from 'i18next'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { NavigateFunction } from 'react-router-dom'
import { usePermissions } from '.'
import { MAX_PAGE_SIZE, SearchReviewType } from '../constants/constants'
import { notificationService, searchReviewService } from '../services'
import { RootState } from '../store'
import {
  Document,
  SearchQuery,
  SearchReview,
  SearchReviewListResponse,
  SearchSource,
  setListCsv,
  setSearchQueryCsv,
  setSearchReviewList,
  setSearchReviewListByIds,
  setSearchReviewListTotalResults,
  setSearchReviewListUiFlags,
  setSearchReviewLoading,
  setSelectedSearchReview,
} from '../store/reducers/searchReviewReducer'
import { SearchMethod } from '../types/generalTypes'
import useDownloadFile from './useDownloadFile'

interface UseSearchReviewProps {
  preventFetch?: boolean
  page?: number
  pageSize?: number
}

const useSearchReviews = ({
  preventFetch = false,
  page,
  pageSize,
}: UseSearchReviewProps) => {
  const dispatch = useDispatch()
  const downloadCsv = useDownloadFile()
  const loading = useSelector((state: RootState) => state.searchReview.loading)

  const searchReviewList: SearchReview[] | undefined = useSelector(
    (state: RootState) => state.searchReview.searchReviewList
  )
  const selectedSearchReview: SearchReview | undefined = useSelector(
    (state: RootState) => state.searchReview.selected
  )
  const selectedSearchQuery: SearchQuery | undefined = useSelector(
    (state: RootState) => state.searchReview.selectedSearchQuery
  )
  const selectedDocument: Document | undefined = useSelector(
    (state: RootState) => state.searchReview.selectedSearchResultItem
  )

  const searchReviewListTotalResults: number | undefined = useSelector(
    (state: RootState) => state.searchReview.searchReviewListTotalResults
  )

  const searchReviewBatchList: SearchReview[] | undefined = useSelector(
    (state: RootState) => state.searchReview.searchReviewListByIds
  )

  const [refreshSearchReviewListDone, setRefreshSearchReviewListDone] =
    useState(false)

  const uiFlag: number | undefined = useSelector(
    (state: RootState) => state.searchReview.uiFlags
  )

  const { isDeletable } = usePermissions({ uiFlag: uiFlag ?? 0 })

  const fetchSearchReviewListByIds = useCallback(
    (searchReviewIds: string[]) => {
      return searchReviewService
        .getSearchReviewListByIds({ searchReviewIds })
        .then((response: SearchReview[]) => {
          dispatch(setSearchReviewLoading(false))
          dispatch(setSearchReviewListByIds(response))
          setRefreshSearchReviewListDone(true)
        })
        .catch((error: AxiosError | Error) => {
          console.error('axios fetch error', error)
          dispatch(setSearchReviewLoading(false))
        })
    },
    [dispatch]
  )

  interface FetchSearchReviewListProps {
    refreshForceCall?: boolean
  }

  const fetchSearchReviewList = useCallback(
    ({ refreshForceCall = false }: FetchSearchReviewListProps) => {
      if (loading || (preventFetch && !refreshForceCall)) return

      dispatch(setSearchReviewLoading(true))

      searchReviewService
        .getSearchReviewList({ page, pageSize })
        .then((response: SearchReviewListResponse) => {
          dispatch(setSearchReviewLoading(false))
          dispatch(setSearchReviewList(response.data))
          dispatch(setSearchReviewListTotalResults(response.totalResults))
          dispatch(setSearchReviewListUiFlags(response.uiFlags || 0))
          setRefreshSearchReviewListDone(true)
        })
        .catch((error: AxiosError | Error) => {
          console.error('axios fetch error', error)
          dispatch(setSearchReviewLoading(false))
        })
    },
    [dispatch, loading, preventFetch, page, pageSize]
  )
  const refreshSearchReviewList = useCallback(() => {
    fetchSearchReviewList({
      refreshForceCall: true,
    })
  }, [fetchSearchReviewList])

  const refreshBatchSearchReviewList = useCallback(
    (searchReviewIds?: string[]) => {
      if (searchReviewIds?.length) {
        fetchSearchReviewListByIds(searchReviewIds)
      } else {
        dispatch(setSearchReviewLoading(false))
        dispatch(setSearchReviewList([]))
        setRefreshSearchReviewListDone(true)
      }
    },
    [dispatch, fetchSearchReviewListByIds]
  )

  useEffect(() => {
    fetchSearchReviewList({
      refreshForceCall: true,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize])

  const updateSelectedSearchReview = useCallback(
    (selectedSearchReview: SearchReview) => {
      dispatch(setSelectedSearchReview(selectedSearchReview))
      const filteredSearchReviewList = searchReviewList
        ? searchReviewList.filter(
            (searchReview) => searchReview.id !== selectedSearchReview.id
          )
        : []
      const newSearchReviewList = [
        ...filteredSearchReviewList,
        selectedSearchReview,
      ]
      dispatch(setSearchReviewList(newSearchReviewList))
    },
    [dispatch, searchReviewList]
  )

  const exportSearchReviewListCsv = useCallback(() => {
    if (searchReviewList) {
      searchReviewService
        .exportSearchReviewList()
        .then((response: string) => {
          const filename = 'search_review_list.csv'
          downloadCsv(response, filename)
          dispatch(setListCsv(response))
        })
        .catch((error: AxiosError) => {
          console.error('Error fetching search review list CSV data', error)
        })
    }
  }, [dispatch, downloadCsv, searchReviewList])

  const exportSearchReviewSearchQueryCsv = useCallback(
    (searchReviewId: string, searchQueryId: string) => {
      searchReviewService
        .exportSearchReviewSearchQueryToCsv({
          searchReviewId,
          searchQueryId,
        })
        .then((response: string) => {
          const filename = `${selectedSearchReview?.title}_${selectedSearchQuery?.searchText}_${selectedSearchQuery?.searchDate}.csv`
          downloadCsv(response, filename)
          dispatch(setSearchQueryCsv(response))
        })
        .catch((error: AxiosError) => {
          console.error('Error fetching search query CSV data', error)
        })
    },
    [dispatch, downloadCsv, selectedSearchReview, selectedSearchQuery]
  )

  interface SummarizeSearchReviewSearchQueryResultProps {
    searchReviewId: string
    resultId: string
  }

  const summarizeSearchReviewSearchQueryResult = useCallback(
    ({
      searchReviewId,
      resultId,
    }: SummarizeSearchReviewSearchQueryResultProps) => {
      searchReviewService
        .summarizeSearchQueryResult({
          searchReviewId,
          resultId,
        })
        .then((response: SearchReview) => {
          if (response) {
            updateSelectedSearchReview(response)
            notificationService.notificationSuccess({
              messageToDisplay: t(
                'searchReviewDetailsPage.summarizationStarted'
              ),
            })
          }
        })
        .catch((error: AxiosError) => {
          console.error('Error fetching summary for search result', error)
        })
    },
    [updateSelectedSearchReview]
  )

  interface CreateSearchReviewProps {
    query: string
    searchMethod?: SearchMethod
    sourceId: string
    period: string
    lastModifiedDateStart?: string
    lastModifiedDateEnd?: string
    reviewTitle: string
    reviewType: SearchReviewType
    navigate: NavigateFunction
  }

  const createSearchReview = useCallback(
    ({
      query,
      searchMethod,
      sourceId,
      period,
      lastModifiedDateStart,
      lastModifiedDateEnd,
      reviewTitle,
      reviewType,
      navigate,
    }: CreateSearchReviewProps) => {
      return searchReviewService
        .createSearchReviewFromSearchQuery({
          reviewType,
          navigate,
          searchQuery: query,
          searchMethod,
          sourceId,
          period,
          lastModifiedDateStart,
          lastModifiedDateEnd,
          title: reviewTitle,
        })
        .then((response: SearchReview) => {
          dispatch(setSelectedSearchReview(response))
          refreshSearchReviewList()
          return response
        })
        .catch((error) => {
          console.error('axios fetch error', error)
          return error
        })
    },
    [dispatch, refreshSearchReviewList]
  )

  interface AddToExistingSearchReview {
    query: string
    searchMethod?: SearchMethod
    sourceId: string
    period: string
    searchReviewId: string
    lastModifiedDateStart?: string
    lastModifiedDateEnd?: string
    navigate: NavigateFunction
  }

  const addToExistingSearchReview = useCallback(
    ({
      query,
      searchMethod,
      sourceId,
      period,
      searchReviewId,
      lastModifiedDateStart,
      lastModifiedDateEnd,
      navigate,
    }: AddToExistingSearchReview) => {
      return searchReviewService
        .saveSearchQueryInSearchReview({
          searchQuery: query,
          searchMethod,
          sourceId,
          period,
          searchReviewId,
          lastModifiedDateStart,
          lastModifiedDateEnd,
          navigate,
     
        })
        .then((response: SearchReview) => {
          dispatch(setSelectedSearchReview(response))
          refreshSearchReviewList()
          return response
        })
        .catch((error) => {
          console.error('axios fetch error', error)
          return error
        })
    },
    [dispatch, refreshSearchReviewList]
  )

  const getSearchQuerySourceIdForSearchReview = useCallback(
    (searchSource: string | SearchSource | undefined) => {
      return searchReviewService.getSearchQuerySourceId(searchSource)
    },
    []
  )

  const getSearchReviewListOwnedOnly = useCallback(() => {
    return searchReviewService
      .getSearchReviewList({
        ownedOnly: true,
        page: 1,
        pageSize: MAX_PAGE_SIZE,
      })
      .then((response: SearchReviewListResponse) => {
        return response.data
      })
      .catch((error: AxiosError | Error) => {
        console.error('axios fetch error', error)
        throw error
      })
  }, [])

  return {
    exportSearchReviewListCsv,
    exportSearchReviewSearchQueryCsv,
    searchReviewList,
    loading,
    refreshSearchReviewListDone,
    refreshSearchReviewList,
    selectedDocument,
    selectedSearchReview,
    selectedSearchQuery,
    summarizeSearchReviewSearchQueryResult,
    updateSelectedSearchReview,
    createSearchReview,
    addToExistingSearchReview,
    getSearchQuerySourceIdForSearchReview,
    refreshBatchSearchReviewList,
    fetchSearchReviewList,
    fetchSearchReviewListByIds,
    searchReviewListTotalResults,
    searchReviewBatchList,
    isDeletable,
    getSearchReviewListOwnedOnly,
  }
}
export default useSearchReviews
